<template>
  <div class="w-full md:w-64 md:min-w-[16rem] md:max-w-[16rem] flex flex-col space-y-3 md:sticky md:top-4 md:self-start">
    <div v-if="isMobileMenuOpen || !isSmallScreen" :class="[
      { 'md:block': true },
      'animate-slide-in'
    ]" class="mc-sidebar-container sidebar-slide-in">
      <div class="space-y-2">
        <div v-if="categories.length === 0" class="flex justify-center py-4">
          <img src="@/assets/loading.gif" alt="Loading..." class="w-8 h-8">
        </div>
        <div v-for="(category, index) in categories" :key="category.category"
          :class="{ 'mc-sidebar-active': selectedCategory === category.category }"
          @click="selectCategory(category)"
          class="mc-sidebar-item mc-sidebar-item-animate"
          :style="{ animationDelay: (index * 0.06) + 's' }">
          <svg width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
               class="w-4 h-4 mr-2 mc-sidebar-icon">
              <path d="M12 2 L22 12 L12 22 L2 12 Z"></path>
           </svg>
          <span class="mc-sidebar-text">{{ removeNumberPrefix(category.category) }}</span>
          <span class="mc-sidebar-count">{{ category.fileCount }}</span>
        </div>
      </div>
    </div>
    <div class="hidden md:block sidebar-slide-in" style="animation-delay: 0.15s">
      <a :href="mciconsUrl" class="block" target="_blank" @click="handleBannerClick">
        <img src="@/assets/banners/banner.png" alt="Banner" class="w-full block transform transition-transform duration-200 hover:scale-105"/>
      </a>
    </div>

    <div class="hidden md:block sidebar-slide-in" style="animation-delay: 0.3s">
      <a href="https://phytormc.com/?affiliate=ccleaf" class="block" target="_blank">
        <img src="@/assets/logos/pythormc.png" alt="Banner2" class="w-full block transform transition-transform duration-200 hover:scale-105"/>
      </a>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted, getCurrentInstance } from 'vue';
import { authenticatedGet } from '../utils/auth';
import { useRouter } from 'vue-router';

const categories = ref([]);
const selectedCategory = ref('All');
const mciconsUrl = getCurrentInstance().appContext.config.globalProperties.$mciconsUrl;
const router = useRouter();

const handleBannerClick = (event) => {
  if (Math.random() < 0.5) {
    event.preventDefault();
    window.open('https://app.ccleaf.com?ref=apollo', '_blank');
  }
};

// Function to remove number prefix from category name
const removeNumberPrefix = (categoryName) => {
  if (categoryName !== 'All') {
    return categoryName.replace(/^[^\s]*\s*/, '');
  }
  return categoryName;
};

const fetchCategories = async () => {
  try {
    const response = await authenticatedGet('/api/categories');
    const totalFileCount = response.data.reduce((sum, category) => sum + category.fileCount, 0);
    categories.value = [{ category: 'All', fileCount: totalFileCount }, ...response.data];
  } catch (error) {
    console.error('Error fetching categories:', error);
    if (error.response?.status === 401) {
      router.push('/login');
    }
  }
};

const selectCategory = (category) => {
  selectedCategory.value = category.category;
  emit('category-selected', category.category);
};

onMounted(() => {
  fetchCategories();
});

defineProps({
  isMobileMenuOpen: Boolean,
  isSmallScreen: Boolean,
  searchQuery: String
});

const emit = defineEmits(['category-selected']);
</script>

<style scoped>
.animate-slide-in {
  animation: slideIn 0.3s ease-out forwards;
}

.animate-slide-out {
  animation: slideOut 0.3s ease-in forwards;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }

  to {
    opacity: 0;
    transform: translateX(-20px);
  }
}

@media (min-width: 768px) {
  .animate-slide-in,
  .animate-slide-out {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

/* Updated sidebar styling to match the pixel design */
.mc-sidebar-container {
  background-color: #0D0D0D;
  border: 2px solid #1D1D1D;
  border-radius: 20px;
  padding: 0.75rem;
}

.mc-sidebar-item {
  display: flex;
  align-items: center;
  background-color: #111111;
  border: none;
  border-radius: 15px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  text-transform: capitalize;
  font-size: 0.9rem;
}

.mc-sidebar-item-animate {
  opacity: 0;
  transform: translateX(-30px);
  animation: sidebarSlideIn 0.4s ease-out forwards;
}

@keyframes sidebarSlideIn {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.mc-sidebar-item:hover {
  color: #00e5ff!important;
  background-color: #333333;
}

.mc-sidebar-active {
  border-left: 4px solid #00e5ff;
  background-color: #1a2d3d;
}

.mc-sidebar-active:hover{
  color: #00e5ff!important;
}

.mc-sidebar-active .mc-sidebar-text {
  color: #00e5ff;
}

.mc-sidebar-icon {
  fill: #999;
  transition: fill 0.2s ease;
  flex-shrink: 0;
}

.mc-sidebar-item:hover .mc-sidebar-icon {
  fill: #00e5ff;
}

.mc-sidebar-active .mc-sidebar-icon {
  fill: #00e5ff;
}

.mc-sidebar-text {
  color: #ccc;
  transition: color 0.2s ease;
  flex: 1;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mc-sidebar-item:hover .mc-sidebar-text {
  color: #00e5ff!important;
}

.mc-sidebar-count {
  background-color: transparent;
  color: #999;
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0;
  margin-left: 0.5rem;
/*   border: 1px solid #444; */
  min-width: 1.5rem;
  text-align: center;
}

.mc-sidebar-active .mc-sidebar-count {
  color: #7adfff;
}

.sidebar-slide-in {
  opacity: 0;
  transform: translateX(-30px);
  animation: sidebarContainerSlideIn 0.4s ease-out forwards;
}

@keyframes sidebarContainerSlideIn {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
